// SEP-2 / E1 — ManageInventory: the shop owner's authoritative stock console, built on the E0
// primitives (ManageEntity + EntityFormSheet + StateTransitionBar + DestructiveActionGuard). Lives in
// the creator App Detail (owner side), NEVER in the customer app — a customer never reads or mutates
// the stock ledger. The owner can add / edit / archive items (SKU, category, on-hand, reorder point,
// unit, location); AUTHORITATIVELY ADJUST stock (receive +Δ / recount = set / write-off −Δ) which
// writes one immutable `adjustments` ledger row AND updates the item's on-hand in a single flow; move
// each item through the inventory lifecycle (in_stock ↔ low ↔ out → discontinued); and read the
// adjustment history with CSV export. Low/out-of-stock is DERIVED from count vs reorder point (honest,
// never stored as a fake number). All writes hit the ownsApp-gated /owner/{items,adjustments} routes.
import { useMemo, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Boxes, Package, SlidersHorizontal, AlertTriangle, ArrowDownUp } from 'lucide-preact';
import { getLifecycle } from '@vibeshell/shared';
import { t, getLocale } from '../lib/i18n';
import { ManageEntity } from './ManageEntity';
import type { EntityActions } from './ManageEntity';
import type { EntityField, FieldValue, FormSubmitResult } from './EntityFormSheet';
import { EntityFormSheet } from './EntityFormSheet';
import { StateTransitionBar } from './StateTransitionBar';
import { DestructiveActionGuard } from './DestructiveActionGuard';

interface Item {
  id: string;
  name?: string;
  sku?: string;
  category?: string;
  count?: number;
  reorderPoint?: number;
  unit?: string;
  location?: string;
  status?: string;
  archived?: boolean;
  [k: string]: unknown;
}
interface Adjustment {
  id: string;
  item?: string;
  itemName?: string;
  delta?: number;
  resulting?: number;
  type?: string;
  note?: string;
  at?: string;
  [k: string]: unknown;
}

const ITEM_FIELDS: readonly EntityField[] = [
  { name: 'name', labelKey: 'shop.inv.fName', type: 'text', required: true, placeholderKey: 'shop.inv.fNamePh' },
  { name: 'sku', labelKey: 'shop.inv.fSku', type: 'text' },
  { name: 'category', labelKey: 'shop.inv.fCategory', type: 'text' },
  { name: 'count', labelKey: 'shop.inv.fCount', type: 'number', min: 0, helpKey: 'shop.inv.fCountHelp' },
  { name: 'reorderPoint', labelKey: 'shop.inv.fReorder', type: 'number', min: 0, helpKey: 'shop.inv.fReorderHelp' },
  { name: 'unit', labelKey: 'shop.inv.fUnit', type: 'text', placeholderKey: 'shop.inv.fUnitPh' },
  { name: 'location', labelKey: 'shop.inv.fLocation', type: 'text' },
];

const ADJUST_FIELDS: readonly EntityField[] = [
  { name: 'type', labelKey: 'shop.inv.adjType', type: 'select', required: true, options: [
    { value: 'receive', labelKey: 'shop.inv.receive' },
    { value: 'count', labelKey: 'shop.inv.recount' },
    { value: 'write_off', labelKey: 'shop.inv.writeOff' },
  ] },
  { name: 'quantity', labelKey: 'shop.inv.quantity', type: 'number', required: true, min: 0, helpKey: 'shop.inv.quantityHelp' },
  { name: 'note', labelKey: 'shop.inv.note', type: 'text', placeholderKey: 'shop.inv.notePh' },
];

/** Derive the honest stock state from the authoritative count vs the reorder point. */
function stockBadge(item: Item): JSX.Element | null {
  if (item.status === 'discontinued') return <span className="pf-badge pf-badge--seed">{t('shop.inv.discontinued')}</span>;
  const c = typeof item.count === 'number' ? item.count : undefined;
  if (c === undefined) return null;
  const rp = typeof item.reorderPoint === 'number' ? item.reorderPoint : 0;
  if (c === 0) return <span className="pf-badge pf-badge--err">{t('shop.inv.out')}</span>;
  if (rp > 0 && c <= rp) return <span className="pf-badge pf-badge--warn">{t('shop.inv.low')}</span>;
  return <span className="pf-badge pf-badge--ok">{t('shop.inv.inStock')}</span>;
}

export function ManageInventory({ appId }: { appId: string }): JSX.Element {
  const inventoryLc = getLifecycle('inventory');
  const [adjusting, setAdjusting] = useState<{ item: Item; patch: (d: Record<string, unknown>) => Promise<boolean> } | null>(null);
  const [historyKey, setHistoryKey] = useState(0);

  async function submitAdjust(values: Record<string, FieldValue>): Promise<FormSubmitResult> {
    if (!adjusting) return { ok: false };
    const { item, patch } = adjusting;
    const type = String(values.type || 'receive');
    const qty = Number(values.quantity ?? NaN);
    if (!Number.isFinite(qty) || qty < 0) return { ok: false, fieldErrors: { quantity: t('own.invalidNumber') } };
    const cur = typeof item.count === 'number' ? item.count : 0;
    const newCount = type === 'receive' ? cur + qty : type === 'write_off' ? Math.max(0, cur - qty) : qty; // 'count' = set
    const delta = newCount - cur;
    try {
      // 1) append the immutable ledger row (item id = FK; itemName for readable history/CSV).
      const post = await fetch(`/v1/apps/${encodeURIComponent(appId)}/owner/adjustments`, {
        method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ data: { item: item.id, itemName: item.name || item.id, delta, resulting: newCount, type, note: String(values.note || ''), at: new Date().toISOString() } }),
      });
      if (!(post.status === 201 || post.ok)) return { ok: false, errorKey: 'shop.inv.adjustFailed' };
      // 2) update the authoritative on-hand on the item itself.
      const ok = await patch({ count: newCount });
      if (!ok) return { ok: false, errorKey: 'shop.inv.adjustPartial' };
      setHistoryKey((k) => k + 1); // refresh the ledger view
      return { ok: true };
    } catch {
      return { ok: false, errorKey: 'shop.inv.adjustFailed' };
    }
  }

  const renderItem = useMemo(() => (row: Item, a: EntityActions<Item>): JSX.Element => {
    const archived = !!row.archived;
    const c = typeof row.count === 'number' ? row.count : undefined;
    const rp = typeof row.reorderPoint === 'number' ? row.reorderPoint : 0;
    const needsReorder = !archived && row.status !== 'discontinued' && c !== undefined && rp > 0 && c <= rp;
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <span className="pf-bubble" aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0 }}><Package size={18} /></span>
          <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>{row.name || t('shop.inv.untitled')}</span>
              {stockBadge(row)}
              {archived && <span className="pf-badge pf-badge--seed">{t('own.archived')}</span>}
            </span>
            <span style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap', fontSize: 12, color: 'var(--fg-muted)' }}>
              <span style={{ fontWeight: 700, color: 'var(--fg)', fontVariantNumeric: 'tabular-nums' }}>{t('shop.inv.onHand', { n: c ?? 0, unit: row.unit || '' })}</span>
              {row.sku && <span>{row.sku}</span>}
              {row.category && <span>{row.category}</span>}
              {row.location && <span>{row.location}</span>}
            </span>
            {needsReorder && (
              <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-warning)' }}>
                <AlertTriangle size={12} /> {t('shop.inv.reorderAlert', { rp })}
              </span>
            )}
          </div>
          <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
            {!archived && (
              <>
                <button type="button" className="pf-btn pf-btn--sm pf-btn--soft" disabled={a.busyId === row.id} aria-label={t('shop.inv.adjustAria', { name: row.name || '' })} onClick={() => setAdjusting({ item: row, patch: (d) => a.patch(row.id, d) })}>
                  <SlidersHorizontal size={13} /> {t('shop.inv.adjust')}
                </button>
                <button type="button" className="pf-btn pf-btn--sm" disabled={a.busyId === row.id} onClick={() => a.edit(row)}>{t('own.edit')}</button>
              </>
            )}
            <DestructiveActionGuard
              entityLabel={row.name || t('shop.inv.thisItem')}
              archived={archived}
              busy={a.busyId === row.id}
              onArchive={() => a.archive(row)}
              onRestore={() => a.restore(row)}
              onDelete={() => a.remove(row)}
            />
          </span>
        </div>
        {!archived && (
          <StateTransitionBar
            lifecycle={inventoryLc}
            status={row.status || 'in_stock'}
            busy={a.busyId === row.id}
            entityLabel={row.name || t('shop.inv.thisItem')}
            dangerStates={['discontinued', 'out_of_stock']}
            i18nPrefix="shop.inv.st"
            onTransition={(to) => void a.setStatus(row.id, to)}
          />
        )}
      </div>
    );
  }, [inventoryLc]);

  const renderAdjustment = (row: Adjustment): JSX.Element => {
    const delta = typeof row.delta === 'number' ? row.delta : 0;
    return (
      <div style={{ ...cardStyle, flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        <span className="pf-bubble" aria-hidden="true" style={{ width: 34, height: 34, flexShrink: 0 }}><ArrowDownUp size={15} /></span>
        <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)' }}>{row.itemName || row.item || t('shop.inv.thisItem')}</span>
          <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
            {row.type ? t(`shop.inv.${row.type === 'write_off' ? 'writeOff' : row.type === 'count' ? 'recount' : row.type}`) : ''}
            {row.note ? ` · ${row.note}` : ''}
            {row.at ? ` · ${new Date(row.at).toLocaleDateString()}` : ''}
          </span>
        </div>
        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontVariantNumeric: 'tabular-nums' }}>
          <span className={`pf-badge ${delta >= 0 ? 'pf-badge--ok' : 'pf-badge--err'}`}>{delta >= 0 ? `+${delta}` : delta}</span>
          <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('shop.inv.resulting', { n: typeof row.resulting === 'number' ? row.resulting : 0 })}</span>
        </span>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ManageEntity<Item>
        appId={appId}
        collection="items"
        titleKey="shop.inv.title"
        icon={<Boxes size={15} />}
        emptyTitleKey="shop.inv.emptyT"
        emptyBodyKey="shop.inv.emptyS"
        emptyIcon={<Boxes size={22} />}
        fields={ITEM_FIELDS}
        creatable
        addLabelKey="shop.inv.add"
        formTitleAddKey="shop.inv.addTitle"
        formTitleEditKey="shop.inv.editTitle"
        csvExport
        renderItem={renderItem}
        unavailableTitleKey="shop.inv.unavailableT"
        errorAuthKey="shop.inv.errAuth"
      />

      {/* authoritative stock-adjust flow — one ledger row + the item on-hand, in a single guided form. */}
      <EntityFormSheet
        open={!!adjusting}
        titleKey="shop.inv.adjustTitle"
        fields={ADJUST_FIELDS}
        appId={appId}
        initialValues={{ type: 'receive' }}
        submitLabelKey="shop.inv.applyAdjust"
        onSubmit={submitAdjust}
        onClose={() => setAdjusting(null)}
      />

      {/* immutable adjustment ledger (append-only history + CSV). key bumps to refetch after an adjust. */}
      <ManageEntity<Adjustment>
        key={historyKey}
        appId={appId}
        collection="adjustments"
        titleKey="shop.inv.historyTitle"
        icon={<ArrowDownUp size={15} />}
        emptyTitleKey="shop.inv.historyEmptyT"
        emptyBodyKey="shop.inv.historyEmptyS"
        emptyIcon={<ArrowDownUp size={22} />}
        csvExport
        archiveFilter={false}
        renderItem={renderAdjustment}
        unavailableTitleKey="shop.inv.unavailableT"
        errorAuthKey="shop.inv.errAuth"
      />
    </div>
  );
}

const cardStyle: JSX.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 8, padding: 12,
  background: 'var(--bg-el)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
};
